type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'silent';

const LEVEL_COLORS: Record<LogLevel, string> = {
    debug: '#6b7280',
    info: '#3b82f6',
    warn: '#eab308',
    error: '#ef4444',
    silent: '',
};

const PREFIX_COLORS = ['#a5b4fc', '#7dd3fc', '#6ee7b7', '#fcd34d', '#f9a8d4', '#c4b5fd', '#fca5a5', '#fdba74'];

function hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = (hash << 5) - hash + str.charCodeAt(i);
        hash |= 0;
    }

    return Math.abs(hash);
}

function getPrefixColor(prefix: string): string {
    return PREFIX_COLORS[hashString(prefix) % PREFIX_COLORS.length];
}

const LEVEL_ORDER: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
    silent: 4,
};

function timestamp(): string {
    const d = new Date();

    return (
        `${String(d.getHours()).padStart(2, '0')}:` +
        `${String(d.getMinutes()).padStart(2, '0')}:` +
        `${String(d.getSeconds()).padStart(2, '0')}:` +
        `${String(d.getMilliseconds()).padStart(3, '0')}`
    );
}

interface LoggerOptions {
    prefix: string;
    level?: LogLevel;
}

export interface Logger {
    debug: (...args: unknown[]) => void;
    info: (...args: unknown[]) => void;
    warn: (...args: unknown[]) => void;
    error: (...args: unknown[]) => void;
    time: (label: string) => void;
    timeEnd: (label: string) => void;
    setLevel: (lvl: LogLevel) => void;
}

export function createLogger(opts: LoggerOptions): Logger {
    let currentLevel: LogLevel = opts.level ?? 'debug';
    const timers = new Map<string, number>();

    function allowed(level: LogLevel): boolean {
        return LEVEL_ORDER[level] >= LEVEL_ORDER[currentLevel];
    }

    function baseFormat(level: LogLevel): { fmt: string; styles: string[] } {
        const fmt = `%c${timestamp()} %c${level.toUpperCase()} %c[${opts.prefix}] `;
        const styles = [
            'color:#9ca3af;font-size:11px',
            `color:${LEVEL_COLORS[level]};font-weight:700`,
            `color:${getPrefixColor(opts.prefix)};font-weight:600`,
        ];

        return {
            fmt,
            styles,
        };
    }

    function log(level: LogLevel, args: unknown[]) {
        if (!allowed(level) || level === 'silent') return;

        const { fmt, styles } = baseFormat(level);

        let fn: (...a: unknown[]) => void;
        switch (level) {
            case 'debug':
                fn = console.log;
                break;
            case 'info':
                fn = console.info;
                break;
            case 'warn':
                fn = console.warn;
                break;
            case 'error':
                fn = console.error;
                break;
            default:
                fn = console.log;
        }

        if (typeof args[0] === 'string' && args[0].includes('%c')) {
            fn(fmt + args[0], ...styles, ...args.slice(1));
        } else {
            fn(fmt, ...styles, ...args);
        }
    }

    function time(label: string) {
        timers.set(label, performance.now());
    }

    function timeEnd(label: string) {
        const start = timers.get(label);
        if (start == null) return;

        const duration = performance.now() - start;
        timers.delete(label);

        const threshold = 500;
        const color = duration > threshold ? '#f87171' : '#38bdf8';

        log('info', [`%c${label}: ${duration.toFixed(1)}ms`, `color:${color};font-weight:600`]);
    }

    return {
        debug: (...args) => log('debug', args),
        info: (...args) => log('info', args),
        warn: (...args) => log('warn', args),
        error: (...args) => log('error', args),
        time,
        timeEnd,
        setLevel: (lvl: LogLevel) => {
            currentLevel = lvl;
        },
    };
}
